import { z } from 'zod';
/** How a compiled field lands in the note it is applied to. */
export var NoteTemplateFieldOperation;
(function (NoteTemplateFieldOperation) {
    NoteTemplateFieldOperation["Append"] = "APPEND";
    NoteTemplateFieldOperation["Insert"] = "INSERT";
    NoteTemplateFieldOperation["Prepend"] = "PREPEND";
    NoteTemplateFieldOperation["Replace"] = "REPLACE";
})(NoteTemplateFieldOperation || (NoteTemplateFieldOperation = {}));
export const NoteTemplateFieldOperationSchema = z.enum(NoteTemplateFieldOperation);
/** Where a compiled value is sourced from, and so which groups a compile may skip. */
export var NoteTemplateDataGroup;
(function (NoteTemplateDataGroup) {
    NoteTemplateDataGroup["EncounterCarry"] = "ENCOUNTER_CARRY";
    NoteTemplateDataGroup["NoteField"] = "NOTE_FIELD";
    NoteTemplateDataGroup["Patient"] = "PATIENT";
    NoteTemplateDataGroup["PrevisitFormData"] = "PREVISIT_FORM_DATA";
    NoteTemplateDataGroup["PrevisitEncounterData"] = "PREVISIT_ENCOUNTER_DATA";
    NoteTemplateDataGroup["ProblemAndOrders"] = "PROBLEM_AND_ORDERS";
})(NoteTemplateDataGroup || (NoteTemplateDataGroup = {}));
export const NoteTemplateDataGroupSchema = z.enum(NoteTemplateDataGroup);
export const NoteTemplateIdParamsSchema = z.object({ noteTemplateId: z.uuid() }).strict();
/**
 * The list filters.
 *
 * `organizationId` filters by the organization a provider's practices belong to, which is not what
 * the name says; `practiceOrganizationId` is the same filter under the name that describes it, and
 * wins when both are sent.
 */
export const NoteTemplateFindAllQuerySchema = z
    .object({
    providerUserId: z.uuid().optional(),
    organizationId: z.uuid().optional(),
    practiceOrganizationId: z.uuid().optional(),
    q: z.string().min(1).optional(),
    page: z.coerce.number().optional(),
    limit: z.coerce.number().optional(),
})
    .strict();
/**
 * One dynamic value a field pulls in. `outputs` maps each output the value produces to the text it
 * is replaced with in the note; fingerprint reads every entry as a string to find the other dynamic
 * values a field depends on, so it has to be present and every value a string.
 */
export const NoteTemplateDynamicValueConfigSchema = z
    .object({
    name: z.string().min(1),
    outputs: z.record(z.string(), z.string()),
    required: z.boolean(),
    form_template_field_config_id: z.uuid().nullable().optional(),
})
    .strict();
export const NoteTemplateFieldUpsertSchema = z
    .object({
    dynamic_value_configs: z.array(NoteTemplateDynamicValueConfigSchema),
    key: z.string().min(1).nullable().optional(),
    section: z.string().min(1),
    note_field: z.string().min(1),
    note_value: z.string().min(1),
    standard_note_field_id: z.string().min(1).nullable().optional(),
    operation: NoteTemplateFieldOperationSchema.optional(),
    placement: z.int().optional(),
})
    .strict();
/**
 * A whole template, sent on create and on update alike: the fields in the body replace the set the
 * template had.
 *
 * `provider_user_id` is a plain string rather than a uuid, because that is what the route has
 * always accepted.
 */
export const NoteTemplateUpsertSchema = z
    .object({
    template: z
        .object({
        name: z.string().min(1),
        provider_user_id: z.string().min(1),
    })
        .strict(),
    fields: z.array(NoteTemplateFieldUpsertSchema),
})
    .strict();
/** Compiles the template against one note, optionally narrowing which data groups are gathered. */
export const NoteTemplateCompileSchema = z
    .object({
    noteId: z.uuid(),
    dataGroups: z.array(NoteTemplateDataGroupSchema).optional(),
})
    .strict();
export const NoteTemplateApplySchema = z.object({ noteId: z.uuid() }).strict();
export const NoteTemplateApplyPrevisitSchema = z.object({ previsitFormSetId: z.uuid() }).strict();
/**
 * Clears a note, or part of one. With neither `section` nor `template` every clearable field in the
 * note goes; with `section` alone the section does; with both, the subsection does.
 */
export const NoteTemplateClearSchema = z
    .object({
    noteId: z.uuid(),
    section: z.string().min(1).nullable().optional(),
    template: z.string().min(1).nullable().optional(),
})
    .strict();
//# sourceMappingURL=template.js.map